/**
 * Bangkok-pinned dates and times — for values the platform states in its own
 * business timezone rather than the viewer's.
 *
 * Lottery times come from SUPERM as timezone-aware ISO strings (e.g. with a
 * +07:00 offset). SUPERM always displays Bangkok wall-clock time, so we format
 * with a pinned Asia/Bangkok timezone (via date-fns-tz) rather than the viewer's
 * locale — this is what keeps our open/close times matching SUPERM's.
 *
 * Referral claim windows are the other kind: svc-commission answers with a
 * Bangkok CALENDAR DAY (`YYYY-MM-DD`), which is not an instant at all and must
 * never be run through a timezone conversion on the way to the screen.
 */
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { dateFnsLocale } from './locale.js';

const TZ = 'Asia/Bangkok';

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Bangkok wall-clock time as "HH:mm" (24h). Empty string if missing/invalid. */
export function formatBangkokTime(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? formatInTimeZone(d, TZ, 'HH:mm') : '';
}

/**
 * Bangkok short date as "27 May", in the viewer's language. Empty string if
 * missing/invalid.
 *
 * The TIMEZONE is pinned and the LANGUAGE is not: the day is the one SUPERM
 * states in Bangkok, but the month name belongs to whoever is reading it.
 * Without `lang` every non-English player read lottery round dates in English
 * (#512). lo/my have no date-fns locale and fall back to enUS (CLAUDE.md §3.9).
 */
export function formatBangkokDate(iso: string | null | undefined, lang: string): string {
  const d = parse(iso);
  return d ? formatInTimeZone(d, TZ, 'd MMM', { locale: dateFnsLocale(lang) }) : '';
}

/** The Bangkok calendar day (`YYYY-MM-DD`) the instant `at` falls in. */
export function bangkokDayKey(at: Date): string {
  return formatInTimeZone(at, TZ, 'yyyy-MM-dd');
}

/**
 * A Bangkok day key rendered in the viewer's language, e.g. "10 Sep 2026".
 *
 * The key is a calendar date, so it is read back from its own three parts into
 * a LOCAL `Date` and never converted. `new Date('2026-09-10')` is UTC midnight,
 * which is the 9th for every viewer west of Greenwich — and a claim window
 * shown a day early is a player standing at a closed counter.
 *
 * Returns the key unchanged when it is not a `YYYY-MM-DD` string: a label is
 * never worth an exception.
 */
export function formatBangkokDayKey(dayKey: string | null | undefined, lang: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey ?? '');
  if (!parts) return dayKey ?? '';
  const d = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  return Number.isNaN(d.getTime()) ? dayKey! : format(d, 'd MMM yyyy', { locale: dateFnsLocale(lang) });
}
